import React from 'react';

/**
 * FT-353f — warning modal shown when a user tries to close a defect that
 * still has an OPEN corrective maintenance job linked to it.
 *
 * The corrective job is considered "open" when it exists in the active
 * maintenance list AND has not yet been completed (zero completions).
 * Archived jobs are treated as already resolved from the defect's POV —
 * the FT-353f cascade elsewhere clears correctiveJobId when the job is
 * archived, so a closed defect+archived job pair shouldn't reach this
 * modal in practice.
 *
 * Contract:
 *   Users may still proceed via [Close anyway] — recording that the
 *   defect was resolved by other means. Cancelling aborts the close.
 *
 * Props:
 *   job              — the linked maintenance job record ({ id, name|title })
 *   onCloseAnyway()  — user confirmed close
 *   onCancel()       — user aborted
 */
export default function CloseWithOpenJobWarning({ job, onCloseAnyway, onCancel }) {
  if (!job) return null;

  const shortId = String(job.id || '').slice(-5);
  const label = job.name || job.title || job.item || 'No description';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      data-testid="ft353f-close-with-open-job-warning"
    >
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Linked corrective job is still open
        </h3>
        <p className="text-sm text-gray-700 mb-2">
          Job JOB-{shortId}: {label}
        </p>
        <p className="text-xs text-gray-500 mb-6">
          Closing this defect will leave the corrective job unresolved. You
          can still close if you want to record the defect as resolved by
          other means.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="ft353f-cancel-close"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onCloseAnyway}
            data-testid="ft353f-close-anyway"
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
          >
            Close anyway
          </button>
        </div>
      </div>
    </div>
  );
}
